import type { ReactNode } from 'react';
import type { FieldReport } from '@cer-autofill/shared';

export function ReportSidebar({
  submission,
  showRoutine,
  onShowRoutine,
}: {
  submission: FieldReport;
  showRoutine: boolean;
  onShowRoutine: (value: boolean) => void;
}) {
  const quantities = submission.hardware.modules.map((row) => row.quantity);
  const total =
    quantities.length && quantities.every((quantity) => quantity !== undefined)
      ? quantities.reduce<number>((sum, quantity) => sum + (quantity ?? 0), 0)
      : undefined;
  return (
    <aside className="report-sidebar" aria-label="Report controls and site information">
      {/* The control is a flex row, but the label must not be that flex
          container. Chromium toggles a checkbox twice when it sits inside a
          flex or grid label, so a controlled box snaps back and looks dead. */}
      <div className="report-sidebar__toggle">
        <input
          id="report-sidebar-show-routine"
          type="checkbox"
          checked={showRoutine}
          onChange={(event) => onShowRoutine(event.target.checked)}
        />
        <label htmlFor="report-sidebar-show-routine">
          Show compliant, not sighted and not applicable items
        </label>
      </div>
      <details open>
        <summary>Site information</summary>
        <dl>
          <dt>Report</dt>
          <dd>{submission.jobId}</dd>
          {submission.comboPartnerJobId && (
            <>
              <dt>Linked report</dt>
              <dd>{submission.comboPartnerJobId}</dd>
            </>
          )}
          <dt>Owner</dt>
          <dd>{submission.cover.customerName || 'Not recorded'}</dd>
          <dt>Address</dt>
          <dd>{submission.cover.siteAddress || 'Not recorded'}</dd>
          {submission.hardware.inverters.map((row, index) => (
            <div key={`inverter-${index}`}>
              <dt>Inverter {index + 1}</dt>
              <dd>{[row.make, row.model].filter(Boolean).join(' · ') || 'Not recorded'}</dd>
            </div>
          ))}
          {submission.hardware.modules.map((row, index) => (
            <div key={`panel-${index}`}>
              <dt>Panel {index + 1}</dt>
              <dd>{[row.make, row.model].filter(Boolean).join(' · ') || 'Not recorded'}</dd>
            </div>
          ))}
          <dt>Number of panels</dt>
          <dd>{total ?? 'Not recorded'}</dd>
        </dl>
      </details>
    </aside>
  );
}

export function ReportSidebarLayout({
  children,
  ...props
}: Parameters<typeof ReportSidebar>[0] & { children: ReactNode }) {
  return (
    <div className="report-with-sidebar">
      <ReportSidebar {...props} />
      <div className="screen report-detail">{children}</div>
    </div>
  );
}
